import React, { FC, useState, useEffect } from 'react';
import DeleteTaskDialog from './DeleteTaskDialog';
import AddNewTaskDialog from './AddNewTaskDialog';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';

interface Task {
  id: string;
  is_active: boolean;
  name: string;
  icon: string;
  value: string;
  client_delivery_count: string;
  total_hours_spent: string;
}

interface ManageTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

function convertSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds % 3600) / 60);

  let result = '';

  if (hours > 0) {
    result += `${hours} hr${hours > 1 ? 's' : ''}`;
  }

  if (minutes > 0) {
    result += `${result ? ' ' : ''}${minutes} min${minutes > 1 ? 's' : ''}`;
  }

  return result || '0 min';
}

const ManageTaskDialog: FC<ManageTaskDialogProps> = ({
  isOpen,
  onClose,
  title,
}) => {
  const { t } = useTranslation('client');
  const [tasks, setTasks] = useState<Task[]>();
  const [tasksCount, setTasksCount] = useState(14);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isTaskRemovalDialogOpen, setIsTaskRemovalDialogOpen] = useState(false);
  const [isAddNewTaskDialogOpen, setIsAddNewTaskDialogOpen] = useState(false);
  const [selectedTaskData, setSelectedTaskData] = useState<Task | null>(null);

  const fetchData = async () => {
    try {
      const tasksResponse = await axiosInstance.get(`/v1/tasks/?page_size=15`);
      const tasksData = tasksResponse.data;
      setTasks(tasksData.results);
      setTasksCount(tasksData.count);
    } catch (error) {
      console.error('Error fetching tasks data:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setIsRemoving(false);
      setIsTaskRemovalDialogOpen(false);
      setIsAddNewTaskDialogOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isTaskRemovalDialogOpen && !isAddNewTaskDialogOpen) {
      fetchData();
    }
  }, [isTaskRemovalDialogOpen, isAddNewTaskDialogOpen]);

  const handleCancelRemove = () => {
    setIsRemoving(!isRemoving);
  };

  const handleTaskRemovalClick = async (task: Task) => {
    const taskResponse = await axiosInstance.get(
      `/v1/tasks/${task.id}/summarize/`
    );
    taskResponse.data.total_hours_spent = convertSeconds(
      taskResponse.data.total_hours_spent
    );
    setSelectedTaskData(taskResponse.data);
    setIsTaskRemovalDialogOpen(true);
  };

  const handleTaskRemovalDialogClose = () => {
    setIsTaskRemovalDialogOpen(false);
  };

  const handleAddNewTaskClick = () => {
    setIsAddNewTaskDialogOpen(true);
  };

  const handleAddNewTaskDialogClose = () => {
    setIsAddNewTaskDialogOpen(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {!isTaskRemovalDialogOpen && !isAddNewTaskDialogOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div
              className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[720px]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
              tabIndex={-1}
            >
              <div className="bg-white">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:mt-0 w-full">
                    <div className="flex justify-between items-center mt-4 mx-6">
                      <h3
                        className="text-subtitle-1 font-medium text-neutral/300"
                        id="modal-headline"
                      >
                        {title}
                      </h3>
                      <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 focus:outline-none"
                      >
                        <span className="sr-only">Close</span>
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <hr className="border-gray-300 mt-4 w-full" />
                    <form>
                      {!isRemoving ? (
                        tasksCount < 15 ? (
                          <button
                            type="button"
                            className="rounded-md bg-surface-card border-primary/300 border-2 border-dashed box-border h-12 w-[672px] flex flex-row items-center justify-center gap-2 mt-6 mx-6"
                            onClick={handleAddNewTaskClick}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 48 48"
                            >
                              <path
                                fill="none"
                                stroke="#30537E"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="m24.06 10l-.036 28M10 24h28"
                              ></path>
                            </svg>
                            <div className="h-6 flex flex-row items-center justify-center">
                              <span className="relative text-primary/300 font-semi-bold text-subtitle-2">
                                {t('manage_task.create_new_task')}
                              </span>
                            </div>
                          </button>
                        ) : (
                          <span className="flex flex-row items-center justify-center h-12 mt-6 text-neutral/75 font-semi-bold text-subtitle-2">
                            {t('manage_task.max_task')}
                          </span>
                        )
                      ) : (
                        <span className="flex flex-row items-center justify-center h-12 mt-6 mx-6">
                          {t('manage_task.remove_task_caption')}
                        </span>
                      )}
                      <div className="grid grid-cols-3 gap-x-2 gap-y-4 p-6">
                        {tasks?.map((task, index) => (
                          <TaskItem
                            key={index}
                            label={task.name}
                            icon={task.icon}
                            isRemoving={isRemoving}
                            onRemove={() => handleTaskRemovalClick(task)}
                          />
                        ))}
                      </div>
                      <div className="px-6 pb-4">
                        <button
                          type="button"
                          className="inline-flex justify-center items-center rounded-md font-medium text-l text-error gap-2"
                          onClick={handleCancelRemove}
                        >
                          {!isRemoving ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 48 48"
                            >
                              <g fill="none" stroke="#de0000" strokeWidth="3">
                                <path
                                  strokeLinejoin="round"
                                  d="m15 12l1.2-7h15.6l1.2 7"
                                />
                                <path strokeLinecap="round" d="M6 12h36" />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m37 12l-2 31H13l-2-31z"
                                  clipRule="evenodd"
                                />
                                <path strokeLinecap="round" d="M19 35h10" />
                              </g>
                            </svg>
                          ) : (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5.83301 5.8335L18.1663 18.1668M18.1663 5.8335L5.83301 18.1668"
                                stroke="#DE0000"
                                strokeWidth="1.5"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          {!isRemoving
                            ? t('manage_task.remove_task')
                            : t('action.cancel')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTaskRemovalDialogOpen && selectedTaskData && (
        <DeleteTaskDialog
          taskName={selectedTaskData.name}
          isOpen={isTaskRemovalDialogOpen}
          onClose={handleTaskRemovalDialogClose}
          fields={[
            {
              label: t('delete_task.task_name'),
              value: selectedTaskData.name,
            },
            {
              label: t('delete_task.total_client'),
              value: selectedTaskData.client_delivery_count || '0',
            },
            {
              label: t('delete_task.total_duration'),
              value: selectedTaskData.total_hours_spent || '0',
            },
          ]}
          taskId={selectedTaskData.id}
        />
      )}

      {isAddNewTaskDialogOpen && (
        <AddNewTaskDialog
          isOpen={isAddNewTaskDialogOpen}
          onClose={handleAddNewTaskDialogClose}
        />
      )}
    </>
  );
};

interface TaskItemProps {
  label: string;
  icon: React.ReactNode;
  isRemoving: boolean;
  onRemove: () => void;
}

const TaskItem: FC<TaskItemProps> = ({ label, icon, isRemoving, onRemove }) => {
  const [iconSrc, setIconSrc] = useState<string | null>(null);

  useEffect(() => {
    const loadIcon = async () => {
      try {
        const importedIcon = await import(
          `../../../assets/icons/tasks/${icon}.svg`
        );
        setIconSrc(importedIcon.default);
      } catch (err) {
        console.error(`Icon not found: ${icon}`, err);
        setIconSrc(null);
      }
    };

    loadIcon();
  }, [icon]);

  const handleRemoveClick = () => {
    onRemove();
  };

  return !isRemoving ? (
    <div
      className={`flex flex-row items-center justify-between px-2 h-16 border rounded-md shadow-sm w-full`}
    >
      <span className="text-neutral/300 text-body-2">{label}</span>

      {iconSrc && (
        <img src={iconSrc} alt={`${label} icon`} className="w-8 h-8" />
      )}
    </div>
  ) : (
    <div className="h-16 rounded-lg justify-start items-center gap-1 inline-flex">
      <div className="h-full w-full px-2 bg-error/10 rounded-tl-lg rounded-bl-lg border border-error/40 justify-start items-center gap-2 flex">
        <div className="self-center text-neutral/300 text-body-2">{label}</div>
      </div>
      <div
        className="w-[38px] h-16 px-2 bg-error rounded-tr-lg rounded-br-lg justify-start items-center gap-1 flex cursor-pointer"
        onClick={handleRemoveClick}
      >
        <div className="w-5 h-5 justify-center items-center flex">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 48 48"
          >
            <g fill="none" stroke="#ffffff" strokeWidth="3">
              <path strokeLinejoin="round" d="m15 12l1.2-7h15.6l1.2 7" />
              <path strokeLinecap="round" d="M6 12h36" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m37 12l-2 31H13l-2-31z"
                clipRule="evenodd"
              />
              <path strokeLinecap="round" d="M19 35h10" />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ManageTaskDialog;

export const importAllIcons = async () => {
  const iconNames = [
    'leave_for_work',
    'prepare_departure',
    'driving',
    'cargo_bulk',
    'cargo_pallet',
    'exchange_cargo',
    'exchange_cargo_pl',
    'wait_for_cargo',
    'unload_bulk',
    'unload_pallet',
    'rest',
    'wait_for_price_reduction',
    'prepare_to_go_home',
    'return_home',
    'walking',
    'banknotes',
    'battery_50',
    'briefcase',
    'clipboard_document_check',
    'clipboard_document_list',
    'document_check',
    'exclamation_triangle',
    'face_frown',
    'face_smile',
    'finger_print',
    'key',
    'moon',
    'photo',
    'star',
    'truck',
    'user_group',
    'wrench_screwdriver',
    'clock',
    'credit_card',
  ];

  const iconsMap: { [key: string]: string } = {};
  await Promise.all(
    iconNames.map(async (name) => {
      const icon = await import(`../../../assets/icons/tasks/${name}.svg`);
      iconsMap[name] = icon.default;
    })
  );

  return iconsMap;
};
